<script lang="ts" setup>
	const serialNumberLocation = [
		'Sticker at the back of the phone or laptop',
		'The battery compartment, if the battery is removable',
		'The phone or laptop settings using the "about" and find the “serial number”.'
	]

	const props = defineProps<{
		isLaptop?: boolean
		show: boolean
	}>()

	defineEmits<{
		(event: 'proceed'): void
		(event: 'update:show', param: boolean): void
	}>()

	const images: Record<string, { default: string }> = import.meta.glob('~/assets/images/png/*.png', { eager: true })

	const gadgetSource = computed(() => ({
		'front view': {
			src: images['/assets/images/png/mobile-front.png']?.default ?? ''
		},
		'Back view': {
			src: images['/assets/images/png/mobile-back.png']?.default ?? ''
		},
		'Phone turned on': {
			src: images['/assets/images/png/mobile-on.png']?.default ?? ''
		},
		'Serial number of phone': {
			src: images['/assets/images/png/mobile-serial.png']?.default ?? ''
		}
	}))
	const laptopSource = computed(() => ({
		'Top view': {
			src: images['/assets/images/png/laptop-top-view.png']?.default ?? ''
		},
		'Bottom view': {
			src: images['/assets/images/png/laptop-bottom-view.png']?.default ?? ''
		},
		'Serial no. on screen': {
			src: images['/assets/images/png/laptop-front-view.png']?.default ?? ''
		},
		'Serial no. on laptop body': {
			src: images['/assets/images/png/laptop-body.png']?.default ?? ''
		}
	}))

	const imgSource = computed<Record<string, { src: string }>>(() =>
		props.isLaptop ? laptopSource.value : gadgetSource.value
	)
</script>

<template>
	<div
		class="overlay flex flex-col items-end justify-end"
		:class="{ invisible: !show }"
		@click.stop="$emit('update:show', false)"
	>
		<div
			class="custom-transition-2 max-h-[80%] w-full overflow-hidden overflow-y-auto rounded-tl-lg rounded-tr-lg bg-white pt-3"
			:class="{
				'h-0': !show,
				'h-[90%]': show
			}"
			@click.stop
		>
			<div class="bg-white">
				<div
					class="mx-auto mb-8 h-[6px] max-w-[100px] rounded-md bg-gray-200"
					@click="$emit('update:show', false)"
				/>
			</div>

			<div class="w-full px-10 pb-5">
				<article class="flex w-full flex-col items-center justify-center">
					<h4 class="text-xl font-semibold capitalize tracking-wide text-blue-950">
						How to inspect your {{ isLaptop ? 'Laptop' : 'Mobile' }}
					</h4>

					<div
						class="00 mx-auto mt-5 min-h-[474px] w-full rounded-lg border border-primary-2 bg-primary-50 px-5 py-8 sm:max-w-[331px] sm:p-5"
					>
						<ul class="grid grid-cols-2 gap-4 border-b-[0.5px] border-gray-300 pb-5">
							<li
								v-for="(item, k) in imgSource"
								:key="k"
								class="flex flex-col items-center justify-center"
							>
								<div class="min-h-[80px] pb-2">
									<img :src="item.src" :alt="k" class="h-[80px] w-full object-contain text-[10px]" />
								</div>
								<div
									class="min-h-[20px] w-full rounded border-[0.5px] border-purple-300 bg-white p-1.5 text-center text-[10px] font-medium capitalize text-purple-500"
								>
									{{ k }}
								</div>
							</li>
						</ul>

						<p class="py-5 text-xs font-medium leading-snug tracking-wide text-gray-600">
							<span class="text-warning-500">Note:</span> Position of serial number varies based on the
							model of your {{ isLaptop ? 'Laptop' : 'Mobile' }}
							<br />
							<br />
							You can locate the serial number through the following:
						</p>

						<ul class="list-outside list-disc pl-2">
							<li
								v-for="(info, index) in serialNumberLocation"
								:key="`serial-info-` + index"
								class="list-item pt-1.5 text-xs font-medium tracking-wide text-purple-500"
							>
								{{ info }}
							</li>
						</ul>
					</div>

					<button
						type="button"
						class="default-btn mt-8 w-full justify-center bg-primary-2 sm:max-w-[331px]"
						@click="$emit('proceed')"
					>
						Continue
					</button>
				</article>
			</div>
		</div>
	</div>
</template>
